// Dependencies
import {FC} from 'react';

// Pages
import {
  AccessPage,
  ChangePasswordPage,
  ConfirmationPage,
  CustomerDocumentPage,
  EsgAreas,
  EsgDocuments,
  EsgPractices,
  FactSheetPage,
  ForgotPasswordPage,
  GeneratorPage,
  HomePage,
  LoginPage,
  ManageWidgetDetailsPage,
  ManageWidgetsPage,
  PDFViewer,
  PDFViewerRoute,
  PDFViewerV2,
  PerformancePage,
  Portfolio,
  ProfilePage,
  SignUpPage,
  TemplateDetailPage,
  TemplateListPage,
  UserListPage,
  ValuePage,
  WidgetAddPage,
  WidgetEditPage,
  SettingsPage,
  SupportPage
} from '../pages';
import { PortfolioUploadPage } from '../pages/PortfolioUpload';
import { PortfolioUploadAddPage } from '../pages/PortfolioUpload/Add';
import { PortfolioUploadViewPage } from '../pages/PortfolioUpload/View';
import { PortfolioUploadEditPage } from '../pages/PortfolioUpload/Edit';

// Resource
import {ROLE} from '../resource';
import {CreateNewTemplate} from '../pages/Template/Detail/create';
import {UserConfigurationPage} from '../pages/User/Configuration';

// Interfaces
interface IRoute {
  path: string;
  role?: ROLE[];
  component: FC;
  page?: string;
  wrapperSx?: any;
  /** Passed to MainLayout — hides page-level horizontal scrollbar. */
  suppressScrollX?: boolean;
}

// Page
export type PageType = 'performance' | 'value' | 'factsheet';

// Routes
export const ROUTES = {
  AUTH: {
    LOGIN: '/',
    SIGN_UP: '/sign-up',
    CONFIRM_REGISTER: '/confirm/register/:token',
    CONFIRM_CHANGE_PASSWORD: '/confirm/change-password/:token',
    FORGOT_PASSWORD: '/forgot-password',
  },
  DASHBOARD: '/dashboard',
  OLD_DASHBOARD: '/old-dashboard',
  CUSTOMER_DOCUMENT: '/customer-document',
  ACCESS: '/access',
  PROFILE: '/profile',
  NEW_DOC: '/new',
  SETTINGS: '/settings',
  CREATE: '/create',
  EDIT: '/edit/:id',
  TEMPLATE: {
    LIST: '/templates',
    DETAIL: '/templates/:id',
  },
  USER: {
    LIST: '/users',
    CONFIG: '/users/:id'
  },
  PDF_VIEWER: '/docs/:id',
  PDF_VIEWER_V2: '/docs-v2',
  MANAGE_WIDGETS: '/manage-widgets',
  WIDGET_DETAILS: '/manage-widgets/:widgetId',
  WIDGET_ADD: '/manage-widgets/add',
  WIDGET_EDIT: '/manage-widgets/:widgetId/edit',
  PORTFOLIO_UPLOAD: '/portfolio-upload',
  PORTFOLIO_UPLOAD_ADD: '/portfolio-upload/add',
  PORTFOLIO_UPLOAD_VIEW: '/portfolio-upload/view/:id',
  PORTFOLIO_UPLOAD_EDIT: '/portfolio-upload/edit/:id',
  SUPPORT: '/support',
};

// Page name
export const PAGE_NAME = {
  [ROUTES.DASHBOARD]: 'Factsheets',
  [ROUTES.TEMPLATE.LIST]: 'Templates',
  [ROUTES.PROFILE]: 'Edit Profile',
  [ROUTES.ACCESS]: 'Access',
  [ROUTES.USER.LIST]: 'Users',
  [ROUTES.CUSTOMER_DOCUMENT]: 'Customer Document',
  [ROUTES.MANAGE_WIDGETS]: 'Manage Widgets',
  [ROUTES.WIDGET_DETAILS]: 'Widget Details',
  [ROUTES.WIDGET_ADD]: 'Add Widget',
  [ROUTES.WIDGET_EDIT]: 'Widget Edit',
  [ROUTES.USER.CONFIG]: 'User Configuration',
  [ROUTES.SETTINGS]: 'Settings',
  [ROUTES.PORTFOLIO_UPLOAD]: 'Portfolio Data Upload',
  [ROUTES.PORTFOLIO_UPLOAD_ADD]: 'Portfolio Upload',
  [ROUTES.PORTFOLIO_UPLOAD_VIEW]: 'Portfolio Upload',
  [ROUTES.PORTFOLIO_UPLOAD_EDIT]: 'Portfolio Upload',
  [ROUTES.SUPPORT]: 'Support',
};

export const getPageName = (pathname) => {
  let route = pathname;
  if (pathname.includes('/manage-widgets')) {
    route = ROUTES.MANAGE_WIDGETS;
  }

  if (pathname.includes(('/users/'))) {
    route = ROUTES.USER.CONFIG;
  }

  if (pathname.includes('/portfolio-upload/view/')) {
    route = ROUTES.PORTFOLIO_UPLOAD_VIEW;
  }
  if (pathname.includes('/portfolio-upload/edit/')) {
    route = ROUTES.PORTFOLIO_UPLOAD_EDIT;
  }

  return PAGE_NAME[route];
}

// Export auth routes
export const AUTH_ROUTES: IRoute[] = [
  {
    path: ROUTES.AUTH.LOGIN,
    component: LoginPage,
  },
  {
    path: ROUTES.AUTH.SIGN_UP,
    component: SignUpPage,
  },
  {
    path: ROUTES.AUTH.CONFIRM_REGISTER,
    component: ConfirmationPage,
  },
  {
    path: ROUTES.AUTH.CONFIRM_CHANGE_PASSWORD,
    component: ChangePasswordPage,
  },
  {
    path: ROUTES.AUTH.FORGOT_PASSWORD,
    component: ForgotPasswordPage,
  },
];

export const Pages = [
  "Factsheets",
  "Users",
  "Customer Documents",
  "Manage Widgets",
  "Portfolio Upload",
  "Support",
];

// Export main routes
export const MAIN_ROUTES: IRoute[] = [
  {
    path: ROUTES.DASHBOARD,
    component: HomePage,
    role: [ROLE.ADMIN, ROLE.CUSTOMER],
    page: "Factsheets",
  },
  {
    path: ROUTES.TEMPLATE.LIST,
    component: TemplateListPage,
    role: [ROLE.ADMIN],
    page: "Template",
  },
  {
    path: ROUTES.TEMPLATE.DETAIL,
    component: TemplateDetailPage,
    role: [ROLE.ADMIN],
    page: "Template",
  },
  {
    path: ROUTES.CUSTOMER_DOCUMENT,
    component: CustomerDocumentPage,
    role: [ROLE.ADMIN, ROLE.CUSTOMER],
    page: "Customer Documents",
  },
  {
    path: ROUTES.ACCESS,
    component: AccessPage,
    role: [ROLE.CUSTOMER],
  },
  {
    path: ROUTES.PROFILE,
    component: ProfilePage,
    role: [ROLE.ADMIN, ROLE.CUSTOMER],
  },
  {
    path: ROUTES.CREATE,
    component: GeneratorPage,
    role: [ROLE.ADMIN, ROLE.CUSTOMER],
  },
  {
    path: ROUTES.EDIT,
    component: GeneratorPage,
    role: [ROLE.ADMIN, ROLE.CUSTOMER],
  },
  {
    path: ROUTES.PDF_VIEWER,
    component: PDFViewerRoute,
    role: [ROLE.ADMIN, ROLE.CUSTOMER],
    suppressScrollX: true,
    wrapperSx: {
      p: 0,
      height: {
        xs: 'calc(100vh - 27px)',
        sm: 'calc(100vh - 51px)',
        md: 'calc(100vh - 47px)',
        lg: 'calc(100vh - 111px)',
      },
    }
  },
  {
    path: ROUTES.PDF_VIEWER_V2,
    component: PDFViewerV2,
    role: [ROLE.ADMIN, ROLE.CUSTOMER],
    suppressScrollX: true,
    wrapperSx: {
      p: 0,
      height: {
        xs: 'calc(100vh - 27px)',
        sm: 'calc(100vh - 51px)',
        md: 'calc(100vh - 47px)',
        lg: 'calc(100vh - 111px)',
      },
    }
  },
  {
    path: ROUTES.USER.LIST,
    component: UserListPage,
    role: [ROLE.ADMIN],
  },
  {
    path: '/performance',
    component: PerformancePage,
  },
  {
    path: '/value',
    component: ValuePage,
  },
  {
    path: '/factsheet',
    component: FactSheetPage,
  },
  {
    path: ROUTES.MANAGE_WIDGETS,
    component: ManageWidgetsPage,
    role: [ROLE.ADMIN, ROLE.CUSTOMER],
    page: "Manage Widgets"
  },
  {
    path: ROUTES.WIDGET_DETAILS,
    component: ManageWidgetDetailsPage,
    role: [ROLE.ADMIN, ROLE.CUSTOMER],
    page: "Manage Widgets"
  },
  {
    path: ROUTES.WIDGET_ADD,
    component: WidgetAddPage,
    role: [ROLE.ADMIN, ROLE.CUSTOMER],
    page: "Manage Widgets"
  },
  {
    path: ROUTES.WIDGET_EDIT,
    component: WidgetEditPage,
    role: [ROLE.ADMIN, ROLE.CUSTOMER],
    page: "Manage Widgets"
  },
  {
    path: ROUTES.USER.CONFIG,
    component: UserConfigurationPage,
    role: [ROLE.ADMIN],
  },
  {
    path: ROUTES.SETTINGS,
    component: SettingsPage,
    role: [ROLE.ADMIN, ROLE.CUSTOMER],
  },
  {
    path: ROUTES.PORTFOLIO_UPLOAD,
    component: PortfolioUploadPage,
    role: [ROLE.ADMIN, ROLE.CUSTOMER],
    page: "Portfolio Upload",
  },
  {
    path: ROUTES.PORTFOLIO_UPLOAD_ADD,
    component: PortfolioUploadAddPage,
    role: [ROLE.ADMIN, ROLE.CUSTOMER],
    page: "Portfolio Upload",
  },
  {
    path: ROUTES.PORTFOLIO_UPLOAD_VIEW,
    component: PortfolioUploadViewPage,
    role: [ROLE.ADMIN, ROLE.CUSTOMER],
    page: "Portfolio Upload",
  },
  {
    path: ROUTES.PORTFOLIO_UPLOAD_EDIT,
    component: PortfolioUploadEditPage,
    role: [ROLE.ADMIN, ROLE.CUSTOMER],
    page: "Portfolio Upload",
  },
  {
    path: ROUTES.SUPPORT,
    component: SupportPage,
    role: [ROLE.ADMIN, ROLE.CUSTOMER],
    page: "Support",
  },
];

// Export work routes
export const WORK_ROUTES: IRoute[] = [
  {
    path: ROUTES.NEW_DOC,
    component: CreateNewTemplate,
  },
];
